import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Calendar, 
  ArrowLeft,
  Send,
  FileText,
  Building,
  Download
} from "lucide-react";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTender } from "@/hooks/api/use-tenders";
import { useAuth } from "@/contexts/AuthContext";
import { clientService } from "@/api/services/client.service";
import { cn } from "@/lib/utils";

const TenderDetails = () => {
  const { t, isRTL, language } = useLanguage();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const tenderId = parseInt(id || '0');
  
  const { data: tenderData, isLoading, error } = useTender(tenderId);
  const tender = tenderData?.data;

  const handleDownloadDocument = async (doc: any) => {
    if (!tender) return;
    
    try {
      await clientService.downloadTenderDocument(
        tender.id,
        doc.id,
        doc.original_name || doc.filename || doc.file_name || doc.name
      );
    } catch (error) {
      console.error('Download failed:', error);
      const downloadUrl = `/api/v1/client/tenders/${tender.id}/documents/${doc.id}/download`;
      window.open(downloadUrl, '_blank');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      closed: {
        label: t('tenders.closed') || 'مغلقة',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
      },
      active: {
        label: language === 'en' ? 'Active' : 'نشط',
        className: '',
      },
      pending: {
        label: t('tenders.pending') || 'قيد المراجعة',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-24" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-20 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !tender) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {t('tenders.tenderNotFound') || 'لم يتم العثور على المناقصة'}
          </p>
          <Link to="/dashboard/tenders">
            <Button className="mt-4" variant="outline">
              <ArrowLeft className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {t('common.back') || 'العودة'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const title = typeof tender.title === 'string' 
    ? tender.title 
    : tender.title?.[language] || tender.title?.en || '';
  const description = tender.description 
    ? (typeof tender.description === 'string' 
        ? tender.description 
        : tender.description?.[language] || tender.description?.en || '')
    : '';
  const requirements = tender.requirements
    ? (typeof tender.requirements === 'string'
        ? tender.requirements
        : tender.requirements?.[language] || tender.requirements?.en || '')
    : '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/tenders">
            <Button variant="outline" size="sm">
              <ArrowLeft className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {t('common.back') || 'العودة'}
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('tenders.tenderDetails') || 'تفاصيل المناقصة'}
            </h1>
          </div>
        </div>
        {getStatusBadge(tender.status)}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tender Info - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('tenders.basicInfo') || 'المعلومات الأساسية'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {title}
                </h3>
                {description && (
                  <p className="text-muted-foreground leading-relaxed">
                    {description}
                  </p>
                )}
              </div>
              
              {requirements && (
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('tenders.requirements') || 'المتطلبات'}
                  </h4>
                  <p className="text-muted-foreground leading-relaxed">
                    {requirements}
                  </p>
                </div>
              )}

              {/* Visibility Info */}
              <div className="flex flex-wrap gap-2">
                {tender.visibility_type === 'specializations' && (
                  <Badge variant="outline">
                    {t('tenders.bySpecializations') || 'حسب الاختصاصات'}
                  </Badge>
                )}
                {tender.visibility_type === 'specific_clients' && (
                  <Badge variant="outline">
                    {t('tenders.specificClients') || 'عملاء محددون'}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Specializations */}
          {tender.specializations && tender.specializations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  {t('tenders.specializations') || 'الاختصاصات'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {tender.specializations.map((spec: any) => {
                    const specName = typeof spec.name === 'string' 
                      ? spec.name 
                      : spec.name?.[language] || spec.name?.en || '';
                    return (
                      <Badge key={spec.id} variant="secondary">
                        {specName}
                      </Badge>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Documents */}
          {tender.documents && tender.documents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {t('tenders.documents') || 'الملفات'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {tender.documents.map((doc: any) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex-shrink-0">
                          <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {doc.original_name || doc.file_name}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {doc.formatted_size || `${Math.round(doc.file_size / 1024)} KB`}
                            </p>
                            {doc.document_type && (
                              <Badge variant="outline" className="text-xs">
                                {doc.document_type}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadDocument(doc)}
                        className="flex-shrink-0"
                      >
                        <Download size={14} className={cn(isRTL ? 'ml-1' : 'mr-1')} />
                        {t('common.download') || 'Download'}
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Deadline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t('tenders.deadline') || 'الموعد النهائي'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tender.deadline && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-2">
                    {(() => {
                      try {
                        const date = new Date(tender.deadline);
                        if (isNaN(date.getTime())) return tender.deadline;
                        return format(date, "PPP", { locale: isRTL ? ar : enUS });
                      } catch {
                        return tender.deadline;
                      }
                    })()}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('tenders.deadlineNote') || 'آخر موعد لتقديم العروض'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Button */}
          {user?.type === 'client' && (
            <Button asChild className="w-full gap-2">
              <Link to={`/dashboard/tenders/${tender.id}/offer`}>
                <Send className="h-4 w-4" />
                {t('tenders.submitOffer') || 'تقديم عرض'}
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenderDetails;
